/**
 * "Notifications explainer" tour.
 *
 * Walks the user through the notification bell, the slide-out panel, the
 * urgency filters, and the events that trigger notifications.
 *
 * Targets used:
 *   data-tour="notif-bell"     — the bell button in the sidebar header
 *   data-tour="notif-panel"    — the slide-out panel (teleported to body)
 *   data-tour="notif-filters"  — the urgency filter bar inside the panel
 *
 * Urgency colours, click behaviour, polling frequency and triggering events
 * mirror Aml.vue (notifFilters around line 187, handleNotifClick around
 * line 454, polling at line 271) and the Aml*Notification listener classes.
 * Keep in sync if those change.
 */

import { NEXT, BACK, START, SKIP, DONE } from './_helpers.js';

function waitForElement(selector, timeout = 10000) {
    return new Promise((resolve) => {
        if (document.querySelector(selector)) return resolve();
        const start = Date.now();
        const check = () => {
            if (document.querySelector(selector)) return resolve();
            if (Date.now() - start > timeout) return resolve();
            setTimeout(check, 50);
        };
        check();
    });
}

// Inline-styled urgency pill matching the colours defined in Aml.vue line 187-194.
const PILL_BASE = 'display:inline-block;font-size:11px;font-weight:700;padding:2px 10px;border-radius:8px;color:#fff;letter-spacing:0.3px;';
const pill = (label, bg) => `<span style="${PILL_BASE}background:${bg};">${label}</span>`;

const allPills = `
    ${pill('Critical', '#dc2626')}
    ${pill('High',     '#ea580c')}
    ${pill('Medium',   '#ca8a04')}
    ${pill('Normal',   '#2563eb')}
    ${pill('Low',      '#6b7280')}
`;

export const notificationsTour = {
    id: 'notifications',
    title: 'Notifications explained',
    description: 'How the bell icon works — urgency levels, what triggers a notification, and what happens when you click one.',
    autoLaunchOn: { page: 'dashboard' },

    steps: [
        {
            id: 'intro',
            title: 'Notifications',
            text: `Notifications surface compliance events that need your attention —
                escalations raised, onboarding completed, training milestones, and so on.
                The system polls for new notifications every 60 seconds.`,
            buttons: [START],
        },
        {
            id: 'bell',
            attachTo: { element: '[data-tour="notif-bell"]', on: 'right' },
            beforeShowPromise: () => waitForElement('[data-tour="notif-bell"]'),
            title: 'The bell icon',
            text: `The bell sits at the top of the sidebar. The red badge shows the count
                of <strong>unread</strong> notifications (capped at "9+"). Click the bell
                to open the slide-out panel.`,
            advanceOn: { selector: '[data-tour="notif-bell"]', event: 'click' },
        },
        {
            id: 'panel',
            attachTo: { element: '[data-tour="notif-panel"]', on: 'left' },
            beforeShowPromise: () => waitForElement('[data-tour="notif-panel"]'),
            cancelIfMissing: '[data-tour="notif-panel"]',
            title: 'The notification panel',
            text: `Each item shows the message, urgency, source page, and time. Unread
                items are highlighted. Clicking a notification will <strong>mark it
                read</strong> and jump you straight to the relevant page (e.g. the
                escalation, the client, the training record).
                <br><br>
                The <em>×</em> on each item dismisses it without navigating, and the
                top-right buttons let you <strong>Mark all read</strong> or
                <strong>Clear all</strong>.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'filters',
            attachTo: { element: '[data-tour="notif-filters"]', on: 'bottom' },
            beforeShowPromise: () => waitForElement('[data-tour="notif-filters"]'),
            cancelIfMissing: '[data-tour="notif-panel"]',
            title: 'Urgency levels',
            text: `Five urgency levels, each with its own colour:
                <div style="margin:10px 0;display:flex;flex-wrap:wrap;gap:6px;">${allPills}</div>
                Filter the list to one urgency at a time using the tabs, or stay on
                <strong>All</strong> to see everything.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'triggers',
            cancelIfMissing: '[data-tour="notif-panel"]',
            title: 'What triggers a notification',
            text: `Notifications are generated by the system in response to compliance
                events. A few of the most common ones:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>Escalation raised</strong> — notifies the Compliance Officer.
                        <em>Critical</em> if it's a sanctions match, otherwise <em>High</em>.</li>
                    <li><strong>Escalation reviewed</strong> — notifies the Senior Manager
                        (<em>High</em>) if pending approval, or the original raiser
                        (<em>Normal</em>) once resolved.</li>
                    <li><strong>Client onboarded</strong> — notifies CO and SM. <em>Critical</em>
                        if high-risk, otherwise <em>Low</em>.</li>
                    <li><strong>Client offboarded</strong> — notifies CO, SM and GB
                        (<em>High</em>).</li>
                    <li><strong>Training completed</strong> — notifies CO (<em>Low</em>).</li>
                </ul>
                Plenty of other events trigger notifications too — periodic-review
                reminders, ID-verification results, PEP/sanctions match assessments,
                program changes, and more. The list above is just a representative
                sample.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'finish',
            cancelIfMissing: '[data-tour="notif-panel"]',
            title: 'That\'s notifications',
            text: `Keep the bell in your peripheral vision — it's how the platform pulls
                you toward anything urgent without you needing to dig around.`,
            buttons: [BACK, DONE],
        },
    ],
};
